import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Mail, Phone, ArrowUpRight } from "lucide-react";
import { useSiteData, useLocaleText } from "../contexts/SiteDataContext";

const IconFacebook = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);
const IconInstagram = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);
const IconLinkedin = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);
const IconX = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const Footer = () => {
  const { t } = useTranslation();
  const { siteContent, services } = useSiteData();
  const { pick } = useLocaleText();

  const phones = siteContent?.phones || ["+974 50871458", "+974 77772905"];
  const email = siteContent?.email || "info@ragimetrdingcontracting.com";
  const crNumber = siteContent?.cr_number || "220664";
  const logo = siteContent?.logo || "/logo.png";
  const location_text = pick(siteContent?.location) || "Doha, Qatar";
  const companyName = pick(siteContent?.company_name) || "RAGIME TRADING & CONTRACTING WLL";

  const otherServices = (services || []).filter((s) => s.type === "other");

  return (
    <footer className="bg-[#111111] text-white relative overflow-hidden" data-testid="site-footer">
      <div className="h-3 diagonal-stripes" />
      <div className="max-w-7xl mx-auto px-6 pt-8 md:pt-10 pb-0">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5 flex flex-col items-center md:items-start text-center md:text-left">
            <div className="mb-4 flex flex-col items-center md:items-start w-full md:w-auto">
              <div className="inline-flex items-center justify-center bg-white p-1.5 rounded">
                <img src={logo} alt="RTC logo" className="h-10 sm:h-12 md:h-11 w-auto object-contain" />
              </div>
              <div className="mt-1.5 text-center md:text-left w-full md:w-auto px-2 md:px-0">
                <div className="font-mono-rtc text-[7px] sm:text-[8px] md:text-[8.5px] uppercase tracking-[0.15em] text-[#FF5A00] font-bold leading-tight whitespace-normal">
                  {t("nav.logoSubtitle")}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <a href="#" aria-label="Facebook" className="w-7 h-7 border border-white/20 flex items-center justify-center text-white/60 hover:text-[#FF5A00] hover:border-[#FF5A00] hover:shadow-[2px_2px_0_#FF5A00] transition-all">
                  <IconFacebook />
                </a>
                <a href="#" aria-label="Instagram" className="w-7 h-7 border border-white/20 flex items-center justify-center text-white/60 hover:text-[#FF5A00] hover:border-[#FF5A00] hover:shadow-[2px_2px_0_#FF5A00] transition-all">
                  <IconInstagram />
                </a>
                <a href="#" aria-label="LinkedIn" className="w-7 h-7 border border-white/20 flex items-center justify-center text-white/60 hover:text-[#FF5A00] hover:border-[#FF5A00] hover:shadow-[2px_2px_0_#FF5A00] transition-all">
                  <IconLinkedin />
                </a>
                <a href="#" aria-label="Twitter / X" className="w-7 h-7 border border-white/20 flex items-center justify-center text-white/60 hover:text-[#FF5A00] hover:border-[#FF5A00] hover:shadow-[2px_2px_0_#FF5A00] transition-all">
                  <IconX />
                </a>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed max-w-md text-sm">{t("footer.about")}</p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 border border-white/30 font-mono-rtc text-[10px] uppercase tracking-widest">
              <span className="w-2 h-2 bg-[#FF5A00]" /> {t("common.crNumber")} {crNumber}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">{t("footer.navigate")}</h4>
            <ul className="space-y-2.5">
              {[
                [t("nav.home"), "/"],
                [t("nav.about"), "/about"],
                [t("nav.services"), "/services"],
                [t("nav.rental"), "/services/rental"],
                [t("nav.gallery"), "/gallery"],
                [t("nav.contact"), "/contact"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="text-white/80 hover:text-[#FF5A00] transition-colors text-sm link-underline" data-testid={`footer-link-${to.replace(/\//g, '-')}`}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">{t("footer.services")}</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/services/rental" className="text-white/80 hover:text-[#FF5A00] transition-colors text-sm" data-testid="footer-service-rental">
                  Rental Equipment
                </Link>
              </li>
              {otherServices.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-white/80 hover:text-[#FF5A00] transition-colors text-sm" data-testid={`footer-service-${s.slug}`}>
                    {pick(s.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono-rtc text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">{t("footer.reach")}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-[#FF5A00]" />
                <span className="text-white/80">{location_text}</span>
              </li>
              {phones.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <Phone size={16} className="mt-0.5 text-[#FF5A00]" />
                  <a href={`tel:${p.replace(/\s/g, "")}`} className="text-white/80 hover:text-white" dir="ltr">{p}</a>
                </li>
              ))}
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-[#FF5A00]" />
                <a href={`mailto:${email}`} className="text-white/80 hover:text-white break-all" data-testid="footer-email" dir="ltr">{email}</a>
              </li>
            </ul>
            <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-[#FF5A00] text-white px-5 py-3 font-bold uppercase tracking-wide text-xs hover:bg-white hover:text-[#111111] transition-colors" data-testid="footer-cta">
              {t("common.requestQuote")} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        <div className="mt-4 pt-2.5 border-t border-white/10 text-xs text-white/50 font-mono-rtc uppercase tracking-widest pb-3">
          <span>© {new Date().getFullYear()} {companyName}. {t("footer.rights")}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
